/**
 * SURVEY.ROUTE.TSX
 * Connect to a specific survey by typing firstname and lastname
 */

import { useEffect, useState } from "react"
import { WithTranslation, withTranslation } from "react-i18next"
import Button from "../components/button"
import Page from "../components/page"
import { RouteComponentProps, useParams, withRouter } from "react-router"
import Loader from "@/components/loader"
import { store } from ".."
import { fetchInvitations, fetchSurvey } from "@/redux/actions"
import TextInput from "@/components/text-input"
import { toast } from "react-toastify"
import i18n from "@/translate/i18n"

interface StateProps extends WithTranslation, RouteComponentProps {}

function SurveyRoute(props: StateProps) {
  const { t } = props

  //Get params : id of the survey
  const { id } = useParams<any>()

  //Display dialog
  const [isLoading, setIsLoading] = useState(true)

  //Error message to display
  const [error, setError] = useState<null | string>(id ? null : t("error_survey_empty"))

  //Form data
  const [firstname, setFirstname] = useState("")
  const [lastname, setLastname] = useState("")

  //List of invitations found (if multiple)
  const [invitations, setInvitations] = useState<any[]>([])

  //Survey info
  const [survey, setSurvey] = useState({
    name: "",
    Account: { colors: { email: "" }, options: { heatmapFilterName: null } }
  })

  //Load survey info on load
  useEffect(() => {
    loadSurvey()

    async function loadSurvey() {
      //Set language
      const searchParams: URLSearchParams = new URLSearchParams(window.location.search)
      const lg: string | null = searchParams.get("language")
      i18n.changeLanguage(lg ? lg : "fr")

      if (id) {
        const response: any = await store.dispatch(fetchSurvey(id))
        if (response.error) {
          setError(t(response.error))
        } else {
          setSurvey(response)
        }
        setIsLoading(false)
      } else {
        setIsLoading(false)
      }
    }
  }, [])

  async function next() {
    if (firstname.length && lastname.length) {
      setIsLoading(true)
      const response: any = await store.dispatch(
        fetchInvitations(firstname.trim(), lastname.trim(), id, survey.Account.options.heatmapFilterName)
      )

      setTimeout(() => {
        setIsLoading(false)
        if (response.length === 0) {
          toast(t("error_user_does_not_match"), { type: "error" })
        } else if (response.length > 1) {
          setInvitations(response)
        } else if (response.length === 1) {
          redirect(response[0].code)
        }
      }, 250)
    } else {
      toast(t("survey_form_name_empty"), { type: "error" })
    }
  }

  function redirect(code) {
    props.history.push("/" + code)
  }

  return (
    <Page>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <p>
          <b>{error}</b>
        </p>
      ) : (
        <div style={{ padding: 20, height: "calc(100vh - 40px)" }}>
          {invitations.length > 1 ? (
            <div>
              <div style={{ textAlign: "center" }}>{t("survey_select_invitations")}</div>
              {invitations.map((invitation) => (
                <div className="card _hover" key={invitation.aid} onClick={() => redirect(invitation.code)}>
                  <div>
                    <b>{invitation.username}</b>
                  </div>
                  <div>{invitation.attribute}</div>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <h2>{survey.name}</h2>
              <p>{t("survey_form_help")}</p>
              <p style={{ color: "grey", fontSize: 12 }}>{t("survey_form_help_2")}</p>

              <TextInput
                title={t("firstname")}
                value={firstname}
                onChange={(e: any) => setFirstname(e.value)}
                autoFocus
              />

              <TextInput title={t("lastname")} value={lastname} onChange={(e: any) => setLastname(e.value)} />

              <div style={{ height: 32 }} />
              <Button isLarge className="primary" onClick={() => next()}>
                {t("question_confirm")}
              </Button>
            </div>
          )}
        </div>
      )}
    </Page>
  )
}

export default withRouter(withTranslation()(SurveyRoute))
